import React, { useState, useRef } from "react";
import {
  Popover,
  Typography,
  Stack,
  Divider,
  Avatar,
  Button,
  Box,
} from "@mui/material";
import { Logout } from "@mui/icons-material";
import { useLogoutMutation } from "../../redux/api/authApi";
import { useNavigate } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../../redux/hook";
import { signOut } from "../../redux/Reducers/authSlice";
import {
  formatRoleLabel,
  getUserAvatarInitials,
  getUserDisplayName,
} from "../../utils/userDisplay";

export default function ProfileTrigger({ alignWithTopbar = false }) {
  const anchorRef = useRef(null);
  const [open, setOpen] = useState(false);

  const handleClose = () => setOpen(false);
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const user = useAppSelector((state) => state.auth?.user);
  const userRole = user?.role;
  const displayName = getUserDisplayName(user);
  const roleLabel = formatRoleLabel(userRole);
  const initials = getUserAvatarInitials(user);
  const [logout] = useLogoutMutation();

  const handleSignOut = async () => {
    handleClose();
    try {
      await logout({}).unwrap();
    } catch (e) {
      // Best-effort: even if server logout fails, we still clear local auth.
    } finally {
      dispatch(signOut());
      navigate("/", { replace: true });
    }
  };

  return (
    <>
      {/* ---- clickable name / avatar row ---- */}
      <Stack
        direction="row"
        alignItems="center"
        spacing={1}
        sx={{ cursor: "pointer", userSelect: "none" }}
        onClick={() => setOpen(true)}
        ref={anchorRef}
      >
        <Avatar sx={{ width: alignWithTopbar ? 36 : 32, height: alignWithTopbar ? 36 : 32, fontSize: 14 }}>
          {initials}
        </Avatar>
        <Box sx={{ display: alignWithTopbar ? { xs: "none", sm: "block" } : undefined }}>
          {alignWithTopbar ? (
            <>
              <Typography fontWeight={700} lineHeight={1.15} fontSize={14}>
                {displayName}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {roleLabel || "—"}
              </Typography>
            </>
          ) : (
            <>
              <Typography variant="body2" fontWeight={600}>
                {displayName}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {roleLabel || "—"}
              </Typography>
            </>
          )}
        </Box>
      </Stack>

      {/* ---- popup ---- */}
      <Popover
        open={open}
        anchorEl={anchorRef.current}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: "0 4px 20px rgba(0,0,0,.12)",
            mt: 1,
            minWidth: 240,
            p: 2,
          },
        }}
      >
        {/* header */}
        <Stack direction="row" spacing={2} alignItems="center" mb={2}>
          <Avatar sx={{ width: 48, height: 48, fontSize: 18 }}>{initials}</Avatar>
          <Box>
            <Typography fontWeight={600}>{displayName}</Typography>
            <Typography variant="body2" color="text.secondary">
              {user?.email || "—"}
            </Typography>
          </Box>
        </Stack>

        <Divider sx={{ mb: 1 }} />

        {/* menu items */}
        <Stack spacing={0.5}>
          <Button
            startIcon={<Logout />}
            fullWidth
            sx={{
              justifyContent: "flex-start",
              textTransform: "none",
              px: 1,
              color: "error.main",
            }}
            onClick={handleSignOut}
          >
            Sign Out
          </Button>
        </Stack>
      </Popover>
    </>
  );
}
